<template>
  <div class="user-company-access-list p-6">
    <div class="mb-6">
      <h3 class="mb-2 text-xl font-semibold">Manage User Company Access</h3>
      <p class="mb-4 text-sm text-gray-600">
        Assign users to specific companies or grant access to all companies with a default role.
      </p>
      <p class="mb-4 text-sm text-gray-600">
        Only internal users (i.e. users associated with the default internal company) are able to be
        granted multi-company access.
      </p>
      <p class="text-xs text-gray-300 italic">
        When granting access to all companies, the default role applies to all companies. You can
        override this with specific company assignments (marked with
        <v-icon size="x-small" class="inline-icon">mdi-star</v-icon>).
      </p>
    </div>

    <v-text-field
      v-model="searchQuery"
      label="Search by name or email"
      variant="outlined"
      density="compact"
      prependInnerIcon="mdi-magnify"
      clearable
      hideDetails
      class="mb-4"
    />

    <v-data-table
      :headers="tableHeaders"
      :items="users"
      :itemsPerPage="25"
      :search="searchQuery"
      class="rf-settings-table-data"
    >
      <template #item.name="{ item }"> {{ item.first_name }} {{ item.last_name }} </template>

      <template #item.role="{ item }">
        <v-chip size="small" :color="getRoleColor(item.role)">
          {{ formatRole(item.role) }}
        </v-chip>
      </template>

      <template #item.company_access="{ item }">
        <div class="company-access-chips">
          <v-chip
            v-if="item.all_companies_access_role"
            color="success"
            size="small"
            class="mr-1 mb-1"
          >
            All Companies ({{ formatRole(item.all_companies_access_role) }})
          </v-chip>

          <v-chip
            v-for="access in item.company_accesses"
            :key="access.company_id"
            size="small"
            class="mr-1 mb-1"
            :color="getRoleChipColor(access.role)"
            :title="item.all_companies_access_role ? 'Override for this company' : ''"
          >
            {{ access.company?.name ?? "Unknown company" }} ({{ formatRoleShort(access.role) }})
            <v-icon v-if="item.all_companies_access_role" size="x-small" end class="override-icon"
              >mdi-star</v-icon
            >
          </v-chip>

          <v-chip v-if="hasNoCompanyAccess(item)" color="error" size="small"> None </v-chip>
        </div>
      </template>

      <template #item.actions="{ item }">
        <v-btn icon="mdi-pencil" variant="text" size="small" @click="openEditDialog(item)" />
      </template>

      <template #no-data>
        <div class="settings-no-data-message">No users found.</div>
      </template>
    </v-data-table>

    <!-- Edit Dialog -->
    <v-dialog v-model="showEditDialog" persistent maxWidth="800" width="750">
      <v-card>
        <v-card-title class="headline">Manage Company Access</v-card-title>
        <v-card-text>
          <RfCompanyAccessManager
            v-if="selectedUser"
            :user="selectedUser"
            @saved="onSaved"
            @cancel="closeEditDialog"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import ApiUsers from "@/apis/ApiUsers";
import RfCompanyAccessManager from "./RfCompanyAccessManager.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import StringUtils from "@/utils/StringUtils";

const toastsStore = useToastsStore();

const users = ref([]);
const showEditDialog = ref(false);
const selectedUser = ref(null);
const searchQuery = ref("");

const tableHeaders = [
  {
    title: "User",
    key: "name",
    sortable: true,
    value: item => `${item.first_name} ${item.last_name}`,
  },
  { title: "Email", key: "email", sortable: true },
  { title: "Role", key: "role", sortable: true },
  { title: "Company Access", key: "company_access", sortable: false },
  { title: "Actions", key: "actions", sortable: false, align: "end" },
];

onMounted(() => {
  loadUsers();
});

const loadUsers = async () => {
  try {
    users.value = await ApiUsers.getInternalPrimaryUsers();
  } catch (error) {
    toastsStore.create({ body: "Failed to load users", type: "error", timeout: 5000 });
  }
};

const openEditDialog = user => {
  selectedUser.value = user;
  showEditDialog.value = true;
};

const closeEditDialog = () => {
  showEditDialog.value = false;
  selectedUser.value = null;
};

const onSaved = () => {
  closeEditDialog();
  loadUsers();
};

const formatRole = role => {
  return StringUtils.titleize(role);
};

const getRoleColor = role => {
  const colorMap = {
    super_admin: "accent",
    admin: "primary",
    member: "secondary",
  };
  return colorMap[role] || "secondary";
};

const hasNoCompanyAccess = user => {
  // User has no access if they have neither all-company access nor specific company accesses
  const hasNoSpecificAccess =
    Array.isArray(user.company_accesses) && user.company_accesses.length === 0;
  const hasNoAllCompanyAccess = !user.all_companies_access_role;
  return hasNoSpecificAccess && hasNoAllCompanyAccess;
};

const formatRoleShort = role => {
  return StringUtils.titleize(role);
};

const getRoleChipColor = role => {
  const colorMap = {
    admin: "primary",
    member: "secondary",
  };
  return colorMap[role] || "secondary";
};
</script>

<style scoped>
.settings-no-data-message {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.company-access-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: 400px;
}

.override-icon {
  font-size: 12px !important;
}

.inline-icon {
  font-size: 12px !important;
  vertical-align: middle;
  display: inline-block;
}
</style>
